(() => {
  'use strict';

  const ready = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  ready(() => {
    const form = document.getElementById('SachetsCoQuoteForm');
    if (!form || form.hasAttribute('data-scq-observed')) return;
    form.setAttribute('data-scq-observed', 'true');

    const field = (selector) => form.querySelector(selector);
    const referenceField = field('[data-scq-reference]');
    const success = field('[data-scq-success]');
    const serverError = field('[data-scq-error]');
    const submitStatus = field('[data-scq-submit-status]');
    const internalTest = new URLSearchParams(window.location.search).get('sc_qa') === '1';

    const createReference = () => {
      const bytes = new Uint8Array(8);
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        window.crypto.getRandomValues(bytes);
      } else {
        for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
      }
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `SCQ-${stamp}-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
    };

    let reference = createReference();
    if (referenceField && !success) referenceField.value = reference;

    const analyticsAllowed = () => {
      try {
        return window.Shopify && window.Shopify.customerPrivacy
          && typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function'
          && window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
      } catch (_) { return false; }
    };

    const publish = (name, details = {}) => {
      if (!analyticsAllowed()) return false;
      try {
        const analytics = window.Shopify && window.Shopify.analytics;
        if (!analytics || typeof analytics.publish !== 'function') return false;
        const result = analytics.publish(`sachetsco:${name}`, {
          schema_version: '1',
          form_version: 'simple-enquiry-20261104-r1',
          form_id: 'SachetsCoQuoteForm',
          is_internal_test: internalTest,
          ...(success ? {} : { form_reference: reference }),
          ...details
        });
        if (result && typeof result.catch === 'function') result.catch(() => {});
        return true;
      } catch (_) { return false; }
    };

    const setValue = (selector, value) => {
      const input = field(selector);
      if (input) input.value = value;
    };

    const safeCampaignToken = (value) => {
      if (!value || !/^[a-zA-Z0-9][a-zA-Z0-9._-]{0,79}$/.test(value) || /\d{7}/.test(value)) return '';
      return value;
    };

    const updateSource = () => {
      setValue('[data-scq-internal-test]', internalTest ? 'Yes - internal website test' : 'No');
      setValue('[data-scq-session-status]', 'Not collected');
      for (const name of ['path', 'referrer', 'utm-source', 'utm-medium', 'utm-campaign']) setValue(`[data-scq-session-${name}]`, '');
      if (analyticsAllowed()) {
        const source = window.SachetsCoAttribution?.read();
        if (source) {
          setValue('[data-scq-session-status]', 'First observed in this tab after analytics permission; 30-minute window; not verified first-touch attribution');
          setValue('[data-scq-session-path]', source.path);
          setValue('[data-scq-session-referrer]', source.referrer);
          setValue('[data-scq-session-utm-source]', source.source);
          setValue('[data-scq-session-utm-medium]', source.medium);
          setValue('[data-scq-session-utm-campaign]', source.campaign);
        }
      }
      setValue('[data-scq-source-status]', 'Not collected');
      for (const selector of ['[data-scq-referrer]', '[data-scq-utm-source]', '[data-scq-utm-medium]', '[data-scq-utm-campaign]']) setValue(selector, '');
      if (!analyticsAllowed()) return;
      try {
        const parameters = new URLSearchParams(window.location.search);
        let host = '';
        if (document.referrer) {
          const url = new URL(document.referrer);
          if (['https:', 'http:'].includes(url.protocol) && /^[a-zA-Z0-9.-]{1,253}$/.test(url.hostname)) host = url.hostname.toLowerCase();
        }
        setValue('[data-scq-referrer]', host);
        setValue('[data-scq-utm-source]', safeCampaignToken(parameters.get('utm_source')));
        setValue('[data-scq-utm-medium]', safeCampaignToken(parameters.get('utm_medium')));
        setValue('[data-scq-utm-campaign]', safeCampaignToken(parameters.get('utm_campaign')));
        setValue('[data-scq-source-status]', 'Immediate referrer and quote-page campaign parameters only; not verified first-touch attribution');
      } catch (_) {}
    };

    const focusMessage = (message) => {
      if (!message) return;
      try { message.focus(); } catch (_) {}
    };

    const withPrivacyReady = (callback) => {
      try {
        if (window.Shopify && !window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
            if (!error) callback();
          });
        } else {
          callback();
        }
      } catch (_) {}
    };

    const draftKey = 'sachetsco:enquiry-draft:v1';
    const pendingKey = 'sachetsco:enquiry-pending:v1';
    const completedKey = 'sachetsco:enquiry-completed:v1';
    const draftLifetime = 30 * 60 * 1000;
    const removeStored = (key) => { try { window.sessionStorage.removeItem(key); } catch (_) {} };
    const readStored = (key) => {
      try {
        const item = JSON.parse(window.sessionStorage.getItem(key));
        if (!item || item.version !== 1 || !Number.isFinite(item.updated) || item.updated > Date.now() || Date.now() - item.updated > draftLifetime) { removeStored(key); return null; }
        return item;
      } catch (_) { return null; }
    };

    if (success) {
      const pending = readStored(pendingKey);
      if (pending) { try { window.sessionStorage.setItem(completedKey, JSON.stringify({ version: 1, updated: Date.now(), reference: pending.reference })); } catch (_) {} }
      removeStored(draftKey);
      removeStored(pendingKey);
      focusMessage(success);
      withPrivacyReady(() => publish('enquiry_confirmation_view', { observation: 'native_confirmation_view_not_unique_receipt' }));
      return;
    }

    const draftFields = Array.from(form.querySelectorAll('[data-scq-draft]'));
    const draftStatus = field('[data-scq-draft-status]');
    const draftTools = field('[data-scq-draft-tools]');
    const clearDraft = field('[data-scq-clear-draft]');
    let storageAvailable = false;
    try {
      window.sessionStorage.setItem('sachetsco:storage-test', '1');
      window.sessionStorage.removeItem('sachetsco:storage-test');
      storageAvailable = true;
    } catch (_) {}
    const status = (state) => { if (draftStatus) draftStatus.textContent = draftStatus.dataset[state] || ''; };
    const storedDraft = storageAvailable ? readStored(draftKey) : null;
    let lastEdit = storedDraft?.updated || 0;
    let draftTimer;
    let restored = false;
    if (storedDraft && storedDraft.fields && typeof storedDraft.fields === 'object') {
      for (const input of draftFields) {
        const value = storedDraft.fields[input.name];
        if (input.type === 'checkbox') {
          if (!input.checked && value === true) { input.checked = true; restored = true; }
        } else if (!input.value && typeof value === 'string') {
          input.value = value.slice(0, input.maxLength > 0 ? input.maxLength : 5000);
          if (input.value) restored = true;
        }
      }
      if (typeof storedDraft.reference === 'string' && /^SCQ-\d{8}-[A-F0-9]{16}$/.test(storedDraft.reference)) reference = storedDraft.reference;
    }
    if (referenceField) referenceField.value = reference;
    if (draftTools) draftTools.hidden = false;
    status(storageAvailable ? (restored ? 'restored' : 'ready') : 'unavailable');
    const expireDraft = () => {
      window.clearTimeout(draftTimer);
      if (lastEdit) draftTimer = window.setTimeout(() => removeStored(draftKey), Math.max(0, draftLifetime - (Date.now() - lastEdit)));
    };
    expireDraft();
    const saveDraft = (edited = false) => {
      if (edited) lastEdit = Date.now();
      if (!storageAvailable || !lastEdit || Date.now() - lastEdit >= draftLifetime) return;
      try {
        const fields = {};
        for (const input of draftFields) fields[input.name] = input.type === 'checkbox' ? input.checked : input.value;
        window.sessionStorage.setItem(draftKey, JSON.stringify({ version: 1, updated: lastEdit, reference, fields }));
        expireDraft();
      } catch (_) { storageAvailable = false; status('unavailable'); }
    };
    form.addEventListener('input', (event) => { if (event.target?.hasAttribute('data-scq-draft')) saveDraft(true); });
    form.addEventListener('change', (event) => { if (event.target?.hasAttribute('data-scq-draft')) saveDraft(true); });
    if (clearDraft) clearDraft.addEventListener('click', () => {
      for (const input of draftFields) {
        if (input.type === 'checkbox') input.checked = false;
        else input.value = '';
      }
      removeStored(draftKey);
      removeStored(pendingKey);
      lastEdit = 0;
      window.clearTimeout(draftTimer);
      reference = createReference();
      if (referenceField) referenceField.value = reference;
      started = false;
      attempted = false;
      submitted = false;
      errors.clear();
      status(storageAvailable ? 'cleared' : 'unavailable');
      if (submitStatus) submitStatus.hidden = true;
      draftFields[0]?.focus();
    });

    focusMessage(serverError);
    updateSource();
    document.addEventListener('visitorConsentCollected', updateSource);
    document.addEventListener('scq:source-updated', updateSource);
    withPrivacyReady(updateSource);

    let started = false;
    let attempted = false;
    let submitted = false;
    const errors = new Set();
    const begin = (event) => {
      if (!event.target || !['INPUT', 'SELECT', 'TEXTAREA'].includes(event.target.tagName)
        || event.target.type === 'hidden' || started) return;
      if (publish('enquiry_form_start')) started = true;
    };
    form.addEventListener('input', begin);
    form.addEventListener('change', begin);
    form.addEventListener('invalid', (event) => {
      const input = event.target;
      const match = (input.id || '').match(/^scq-(company|name|email|service|product-form|category|supply-stage|pack-format|fill|quantity-skus|summary|website)-/);
      const name = match ? match[1] : 'other';
      const validity = input.validity || {};
      const reason = validity.valueMissing ? 'required' : validity.typeMismatch ? 'format' : 'invalid';
      const key = `${name}:${reason}`;
      if (!errors.has(key) && publish('enquiry_validation_error', { field: name, reason })) errors.add(key);
    }, true);

    form.addEventListener('submit', () => {
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;
      if (referenceField) referenceField.value = reference;
      updateSource();
      saveDraft();
      try { window.sessionStorage.setItem(pendingKey, JSON.stringify({ version: 1, updated: Date.now(), reference })); } catch (_) {}
      submitted = true;
      if (submitStatus) submitStatus.hidden = false;
      if (!attempted && publish('enquiry_submit_attempt')) attempted = true;
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted && submitted) {
        if (submitStatus) submitStatus.hidden = true;
        const completed = readStored(completedKey);
        if (completed?.reference === reference) {
          for (const input of draftFields) {
            if (input.type === 'checkbox') input.checked = false;
            else input.value = '';
          }
          lastEdit = 0;
          window.clearTimeout(draftTimer);
          removeStored(draftKey);
          status(storageAvailable ? 'cleared' : 'unavailable');
        }
        reference = createReference();
        if (referenceField) referenceField.value = reference;
        started = false;
        attempted = false;
        submitted = false;
        errors.clear();
      }
      updateSource();
    });
  });
})();

